import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { hasAiCoachingAccess } from '../lib/coachAccess';
import { PRO_TIER_NAME } from '../lib/productTier';
import {
  BottomSheet,
  PremiumCard,
  PremiumEmpty,
  PremiumPage,
  PrimaryButton,
  SectionHeader,
} from '../components/premium';
import { CircleListCard, CirclesSkeleton } from '../components/circles/CircleCards';
import {
  circleApiErrorMessage,
  createCircle,
  fetchCircleLimits,
  fetchCircles,
  joinCircle,
} from '../lib/circles/api';
import type { CircleLimits, CircleListItem } from '../types/circles';
import { fieldControl } from '../lib/uiClasses';

export function CirclesPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [circles, setCircles] = useState<CircleListItem[]>([]);
  const [limits, setLimits] = useState<CircleLimits | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.circlesEnabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setNote(null);
    try {
      const [items, lim] = await Promise.all([fetchCircles(), fetchCircleLimits()]);
      setCircles(items);
      setLimits(lim);
    } catch (e) {
      setNote(circleApiErrorMessage(e, 'Could not load circles.'));
      setCircles([]);
    } finally {
      setLoading(false);
    }
  }, [user?.circlesEnabled]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate() {
    if (!name.trim()) return;
    setBusy(true);
    setNote(null);
    try {
      await createCircle(name.trim(), description.trim() || undefined);
      setCreateOpen(false);
      setName('');
      setDescription('');
      await load();
    } catch (e) {
      setNote(circleApiErrorMessage(e, 'Could not create circle.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleJoin() {
    if (!inviteCode.trim()) return;
    setBusy(true);
    setNote(null);
    try {
      await joinCircle(inviteCode.trim());
      setJoinOpen(false);
      setInviteCode('');
      await load();
    } catch (e) {
      setNote(circleApiErrorMessage(e, 'Could not join circle.'));
    } finally {
      setBusy(false);
    }
  }

  if (!user?.circlesEnabled) {
    return (
      <PremiumPage title="Circles" onBack={() => nav('/more')}>
        <PremiumEmpty
          icon={<span className="text-lg">👥</span>}
          title="Circles is turned off"
          action={
            <Link to="/profile">
              <PrimaryButton>Enable in Profile</PrimaryButton>
            </Link>
          }
        />
        <p className="mt-3 text-center text-sm text-zinc-500">
          Opt in under Profile → Circles to join private friend groups.
        </p>
      </PremiumPage>
    );
  }

  const premium = hasAiCoachingAccess(user);
  const atLimit = limits != null && !limits.canJoinMore;

  return (
    <PremiumPage title="Circles">
      <PremiumCard variant="hero" className="!overflow-hidden !p-0">
        <div className="relative bg-gradient-to-br from-violet-600/40 via-[#0a1628] to-sky-600/30 p-6">
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-widest text-sky-300/90">Social accountability</div>
            <h2 className="mt-2 font-display text-2xl font-bold leading-tight text-white">
              Compete on your plan.
              <br />
              Not who lifts the most.
            </h2>
            <div className="mt-5 flex flex-wrap gap-2">
              <PrimaryButton
                className="!min-h-[44px]"
                disabled={atLimit}
                onClick={() => setCreateOpen(true)}
              >
                Create circle
              </PrimaryButton>
              <button
                type="button"
                disabled={atLimit}
                onClick={() => setJoinOpen(true)}
                className="btn-press rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
              >
                Join with code
              </button>
            </div>
          </div>
        </div>
      </PremiumCard>

      {atLimit && !premium ? (
        <PremiumCard className="!border-amber-500/25 !bg-amber-500/10 !p-4">
          <p className="text-sm text-amber-100">
            Free accounts can join <strong>one Circle</strong>. Upgrade to {PRO_TIER_NAME} to unlock multiple Circles.
          </p>
          <Link to="/coach" className="mt-2 inline-block text-xs font-semibold text-amber-200">
            View {PRO_TIER_NAME} →
          </Link>
        </PremiumCard>
      ) : null}

      {note ? <p className="text-sm text-amber-300">{note}</p> : null}

      <SectionHeader title="Your circles" />
      {loading ? (
        <CirclesSkeleton />
      ) : circles.length === 0 ? (
        <PremiumEmpty
          icon={<span className="text-lg">👥</span>}
          title="No circles yet"
          action={
            <PrimaryButton disabled={atLimit} onClick={() => setCreateOpen(true)}>
              Create your first circle
            </PrimaryButton>
          }
        />
      ) : (
        <div className="space-y-3">
          {circles.map((c, i) => (
            <CircleListCard key={c.id} circle={c} delay={i * 0.05} onClick={() => nav(`/circles/${c.id}`)} />
          ))}
        </div>
      )}

      <BottomSheet open={createOpen} onClose={() => setCreateOpen(false)} title="Create circle">
        <div className="grid gap-3">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-zinc-400">Circle name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Summer Cut Squad" className={fieldControl} />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-zinc-400">Description (optional)</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)} className={fieldControl} />
          </label>
          <PrimaryButton fullWidth disabled={busy || !name.trim()} onClick={() => void handleCreate()}>
            {busy ? 'Creating…' : 'Create circle'}
          </PrimaryButton>
        </div>
      </BottomSheet>

      <BottomSheet open={joinOpen} onClose={() => setJoinOpen(false)} title="Join circle">
        <div className="grid gap-3">
          <p className="text-sm text-[var(--text-muted)]">Enter the invite code from a friend.</p>
          <input
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            className={`${fieldControl} !text-center !text-lg !tracking-widest`}
            maxLength={8}
          />
          <PrimaryButton fullWidth disabled={busy || inviteCode.trim().length < 4} onClick={() => void handleJoin()}>
            {busy ? 'Joining…' : 'Join circle'}
          </PrimaryButton>
        </div>
      </BottomSheet>
    </PremiumPage>
  );
}
